import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, ArrowLeft, PartyPopper, ExternalLink, Mail, MessageSquare, Phone, Tag } from "lucide-react";

const toE164 = (p) => { const d = (p || "").replace(/\D/g, ""); return d.startsWith("55") && d.length >= 12 ? d : `55${d}`; };
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { maskCpf, maskPhone, validateCpf, onlyDigits, brl, apiError, SPORT_LABEL } from "@/lib/format";

const empty = { full_name: "", cpf: "", birth_date: "", gender: "M", whatsapp: "", email: "", shirt_size: "M" };

export default function RegisterDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [discount, setDiscount] = useState({ code: "", percent: null });
  const selectedCatObj = cats.find((c) => c.id === selectedCat);
  const applyDiscount = async () => {
    if (!discount.code.trim()) return;
    try {
      const { data } = await api.post("/public/discount/validate", { code: discount.code.trim().toLowerCase() });
      setDiscount((d) => ({ ...d, percent: data.percent }));
      toast.success(`Desconto de ${data.percent}% aplicado!`);
    } catch (e) { setDiscount((d) => ({ ...d, percent: null })); toast.error(apiError(e)); }
  };
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setStep(1); setForm(empty); setCats([]); setSelectedCat(null); setResult(null); setDiscount({ code: "", percent: null });
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goCategories = async () => {
    if (!form.full_name.trim()) return toast.error("Informe o nome completo");
    if (!validateCpf(form.cpf)) return toast.error("CPF inválido");
    if (!form.birth_date) return toast.error("Informe a data de nascimento");
    if (!form.accept_regulation) return toast.error("É necessário aceitar o Regulamento Oficial");
    setLoading(true);
    try {
      const { data } = await api.post("/public/categories/eligible", {
        birth_date: form.birth_date,
        gender: form.gender,
      });
      if (!data.categories.length) {
        toast.error("Nenhuma modalidade disponível para sua idade/sexo");
      } else {
        setCats(data.categories);
        setSelectedCat(data.categories[0].id);
        setStep(2);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/register", {
        full_name: form.full_name,
        accept_regulation: !!form.accept_regulation,
        cpf: onlyDigits(form.cpf),
        birth_date: form.birth_date,
        gender: form.gender,
        whatsapp: form.whatsapp || null,
        email: form.email || null,
        category_id: selectedCat,
        discount_code: discount.percent ? discount.code.trim().toLowerCase() : null,
        shirt_size: form.shirt_size,
      });
      setResult(data);
      setStep(3);
      toast.success("Pré-inscrição realizada!");
      onDone && onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const isKids = (() => {
    if (!form.birth_date) return false;
    const ref = new Date("2026-11-08"); const b = new Date(form.birth_date);
    let age = ref.getFullYear() - b.getFullYear();
    if (ref.getMonth() < b.getMonth() || (ref.getMonth() === b.getMonth() && ref.getDate() < b.getDate())) age--;
    return age <= 13;
  })();
  useEffect(() => { if (isKids && discount.code) setDiscount({ code: "", percent: null }); }, [isKids]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="register-dialog"
        className="bg-surface border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tight">
            {step === 3 ? "Pagamento PIX" : "Inscrição RUN CEMOC"}
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        {step < 3 && (
          <div className="flex items-center gap-2 mb-2">
            {[1, 2].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "bg-brand" : "bg-white/10"}`} />
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 animate-fade-up">
            <div>
              <Label className="text-slate-300">Nome Completo *</Label>
              <Input data-testid="reg-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                className="bg-ink border-white/10 mt-1" placeholder="Seu nome" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">CPF *</Label>
                <Input data-testid="reg-cpf" value={form.cpf} onChange={(e) => set("cpf", maskCpf(e.target.value))}
                  className="bg-ink border-white/10 mt-1" placeholder="000.000.000-00" />
              </div>
              <div>
                <Label className="text-slate-300">Nascimento *</Label>
                <Input data-testid="reg-birth" type="date" value={form.birth_date}
                  onChange={(e) => set("birth_date", e.target.value)} className="bg-ink border-white/10 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Sexo *</Label>
                <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger data-testid="reg-gender" className="bg-ink border-white/10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    <SelectItem value="M">Masculino</SelectItem>
                    <SelectItem value="F">Feminino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Camiseta</Label>
                <Select value={form.shirt_size} onValueChange={(v) => set("shirt_size", v)}>
                  <SelectTrigger data-testid="reg-shirt" className="bg-ink border-white/10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    {["PP", "P", "M", "G", "GG"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">WhatsApp</Label>
                <Input data-testid="reg-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", maskPhone(e.target.value))}
                  className="bg-ink border-white/10 mt-1" placeholder="(00) 00000-0000" />
              </div>
              <div>
                <Label className="text-slate-300">E-mail</Label>
                <Input data-testid="reg-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  className="bg-ink border-white/10 mt-1" placeholder="voce@email.com" />
              </div>
            </div>
            {!isKids && <div className="rounded-xl border-2 border-dashed border-neon/50 bg-neon/5 p-3 space-y-2" data-testid="discount-box">
              <Label className="text-neon text-xs uppercase tracking-widest font-bold flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Tem cupom de desconto? Digite aqui</Label>
              <div className="flex gap-2">
                <Input data-testid="discount-input" value={discount.code} onChange={(e) => setDiscount({ code: e.target.value, percent: null })}
                  placeholder="ex.: cemoc10" className="bg-ink border-white/10 font-mono uppercase" />
                <Button data-testid="discount-apply-btn" type="button" onClick={applyDiscount} className="bg-neon/20 text-neon border border-neon/40 hover:bg-neon/30 font-bold">Aplicar</Button>
              </div>
              {discount.percent && <p data-testid="discount-ok" className="text-xs text-emerald-300 font-bold">✓ {discount.percent}% de desconto aplicado</p>}
            </div>}
            <label data-testid="reg-accept-label" className="flex items-start gap-3 rounded-xl border border-white/10 bg-ink p-3 text-sm cursor-pointer">
              <input data-testid="reg-accept-regulation" type="checkbox" checked={!!form.accept_regulation} onChange={(e) => set("accept_regulation", e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2FA34B]" />
              <span className="text-slate-300">Li e aceito o <a href="/regulamento-oficial-cemoc-run.pdf" target="_blank" rel="noopener noreferrer" className="text-brand font-bold underline" data-testid="reg-regulation-link">Regulamento Oficial</a> (inscrição pessoal e intransferível; reembolso em até 7 dias; termo de responsabilidade na retirada do kit).</span>
            </label>
            <Button data-testid="reg-next-btn" onClick={goCategories} disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover text-white font-bold uppercase tracking-wide">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Ver Modalidades <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 animate-fade-up">
            <p className="text-sm text-slate-400">Selecione sua modalidade elegível:</p>
            <div className="space-y-2">
              {cats.map((c) => (
                <button
                  key={c.id}
                  data-testid={`reg-cat-${c.code_prefix}`}
                  onClick={() => setSelectedCat(c.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedCat === c.id ? "border-brand bg-brand/10" : "border-white/10 bg-ink hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-lg uppercase">{c.name}</div>
                      <div className="text-xs text-slate-400">{SPORT_LABEL[c.sport_type]} • {c.remaining} vagas restantes</div>
                    </div>
                    <div className="font-mono font-bold text-brand">{discount.percent ? <><span className="line-through text-slate-500 text-xs mr-1">{brl(c.price)}</span>{brl(c.price * (1 - discount.percent / 100))}</> : brl(c.price)}</div>
                  </div>
                </button>
              ))}
            </div>
            {discount.percent ? (
              <p data-testid="discount-summary" className="text-xs text-emerald-300 font-bold rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">✓ Cupom <span className="font-mono uppercase">{discount.code}</span> — {discount.percent}% de desconto{selectedCatObj ? ` — você paga ${brl(selectedCatObj.price * (1 - discount.percent / 100))}` : ""}</p>
            ) : selectedCatObj?.sport_type === "KIDS" ? null : (
              <button type="button" data-testid="discount-back-link" onClick={() => setStep(1)} className="text-xs text-slate-400 hover:text-neon underline">Tem cupom de desconto? Volte e informe na etapa anterior</button>
            )}
            <div className="flex gap-2 pt-1">
              <Button data-testid="reg-back-btn" variant="outline" onClick={() => setStep(1)}
                className="border-white/15 bg-transparent hover:bg-white/5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Button data-testid="reg-submit-btn" onClick={submit} disabled={loading}
                className="flex-1 bg-brand hover:bg-brand-hover text-white font-bold uppercase tracking-wide">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Pré-inscrição"}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4 animate-fade-up">
            <div className={`rounded-xl p-4 flex items-start gap-3 ${result.courtesy ? "bg-amber-500/10 border border-amber-500/40" : "bg-emerald-500/10 border border-emerald-500/30"}`} data-testid={result.courtesy ? "courtesy-banner" : "prereg-banner"}>
              <PartyPopper className={`w-5 h-5 mt-0.5 ${result.courtesy ? "text-amber-300" : "text-emerald-400"}`} />
              <div>
                <p className={`font-semibold ${result.courtesy ? "text-amber-200" : "text-emerald-300"}`}>{result.courtesy ? "Inscrição cortesia confirmada!" : "Pré-inscrição registrada!"}</p>
                <p className="text-sm text-slate-300">Número de peito: <span className="font-mono font-bold text-white">{result.bib_code}</span></p>
                {result.courtesy && <p className="text-xs text-slate-400 mt-1">Sem custo — status <b className="text-emerald-300">Pago</b>. Nenhum pagamento é necessário.</p>}
              </div>
            </div>

            {result.initial_password && (
              <div className="rounded-xl bg-ink border border-white/10 p-4 text-sm">
                <p className="text-slate-400 mb-1">Acesso ao Portal do Atleta:</p>
                <p>Login (CPF): <span className="font-mono text-white">{maskCpf(result.cpf)}</span></p>
                <p>Senha inicial: <span data-testid="reg-initial-password" className="font-mono text-brand font-bold">{result.initial_password}</span></p>
              </div>
            )}

            {result.notifications && (
              <div data-testid="notify-status" className="rounded-xl bg-ink border border-white/10 p-4 text-sm space-y-2">
                <p className="text-slate-400">Confirmação da inscrição enviada para:</p>
                <div className="flex flex-wrap gap-2">
                  {[["email", "E-mail", Mail], ["sms", "SMS", MessageSquare], ["whatsapp", "WhatsApp", Phone]].map(([k, l, Icon]) => {
                    const s = result.notifications[k];
                    const cls = s === "sent" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : s === "skipped" ? "bg-white/5 text-slate-500 border-white/10" : "bg-amber-500/15 text-amber-300 border-amber-500/30";
                    const txt = s === "sent" ? "enviado" : s === "skipped" ? "não informado" : s === "failed" ? "falhou" : "em breve";
                    return <span key={k} data-testid={`notify-${k}`} className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border ${cls}`}><Icon className="w-3 h-3" /> {l}: {txt}</span>;
                  })}
                </div>
                {result.notifications.whatsapp !== "sent" && form.whatsapp && (
                  <a data-testid="whatsapp-self-btn" target="_blank" rel="noopener noreferrer"
                    href={`https://wa.me/${toE164(form.whatsapp)}?text=${encodeURIComponent(result.confirmation_text || "")}`}
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#5EF08F] hover:underline">
                    <Phone className="w-3 h-3" /> Receber confirmação no meu WhatsApp agora
                  </a>
                )}
              </div>
            )}

            {result.pix && <>
            {result.pix.payment_link ? (
              <a data-testid="payment-link-btn" href={result.pix.payment_link} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center w-full h-14 rounded-md bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide text-white text-base">
                <ExternalLink className="w-5 h-5 mr-2" /> Pagar {brl(result.pix.amount)} via PIX (Nubank)
              </a>
            ) : (
              <div data-testid="payment-link-missing" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">
                Valor: <b>{brl(result.pix.amount)}</b>. O link de pagamento para este valor ainda não está disponível — acesse o Portal do Atleta mais tarde ou fale com a organização.
              </div>
            )}

            <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4">
              <li>O pagamento é feito pelo checkout seguro do Nubank (PIX gerado automaticamente no valor exato).</li>
              <li>Após pagar, envie o comprovante no Portal do Atleta. O Staff validará e o status mudará para <b>Confirmado</b>.</li>
              <li>O link de pagamento também fica disponível no Portal do Atleta.</li>
            </ul>
            </>}

            <div className="flex gap-2">
              <Button data-testid="pix-goto-login-btn" onClick={() => { onOpenChange(false); navigate("/login"); }}
                className="flex-1 bg-brand hover:bg-brand-hover font-bold uppercase flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Acessar Portal
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
